import { ComponentType, ReactNode, SVGProps } from 'react';

import {
  BookOpenIcon,
  CheckMarkIcon,
  FaceWinkIcon,
  FileUploadIcon,
  GlobeIcon,
  SettingsIcon,
  UsersIcon,
  VoiceIcon,
} from '@braskai/icons';

import { Translate } from '@/shared/i18n';

export type PaywallBenefit = {
  title: string;
  description: string;
  icon: ReactNode;
};

// Figma "base/border" #e5e5e5 has no ui-kit palette token
export const PAYWALL_CARD_BORDER_COLOR = '#E5E5E5';

export const PAYWALL_CARD_SHADOW = '0px 12px 8px rgba(16, 24, 40, 0.08), 0px -2px 3px rgba(16, 24, 40, 0.03)';

export const ENTERPRISE_PLAN_NAME = 'Enterprise';

export const DEFAULT_ALLOWED_PLAN_NAMES = ['Creator Pro', 'Business'];

const createIcon = (IconComponent: ComponentType<SVGProps<SVGSVGElement>>) => <IconComponent height={20} width={20} />;

type PaywallPlanBenefits = {
  features: PaywallBenefit[];
  minutesDescription: string;
};

export const getPaywallContent = (t: Translate) => {
  const allPlansInclude = [
    t('trialPaywall.include.aiDubbing'),
    t('trialPaywall.include.voiceCloning'),
    t('trialPaywall.include.transcriptionTranslation'),
    t('trialPaywall.include.editor'),
  ];

  const chipLabels: Record<string, string> = {
    Business: t('trialPaywall.chip.business'),
    Creator: t('trialPaywall.chip.creator'),
    'Creator Pro': t('trialPaywall.chip.creatorPro'),
  };

  const planBenefits: Record<string, PaywallPlanBenefits> = {
    Business: {
      features: [
        {
          title: t('trialPaywall.benefit.business.marketsTitle'),
          description: t('trialPaywall.benefit.business.marketsDescription'),
          icon: createIcon(GlobeIcon),
        },
        {
          title: t('trialPaywall.benefit.business.terminologyTitle'),
          description: t('trialPaywall.benefit.business.terminologyDescription'),
          icon: createIcon(BookOpenIcon),
        },
        {
          title: t('trialPaywall.benefit.business.teamTitle'),
          description: t('trialPaywall.benefit.business.teamDescription'),
          icon: createIcon(UsersIcon),
        },
      ],
      minutesDescription: t('trialPaywall.benefit.business.minutesDescription'),
    },
    Creator: {
      features: [
        {
          title: t('trialPaywall.benefit.creator.projectsTitle'),
          description: t('trialPaywall.benefit.creator.projectsDescription'),
          icon: createIcon(FaceWinkIcon),
        },
        {
          title: t('trialPaywall.benefit.creator.voiceTitle'),
          description: t('trialPaywall.benefit.creator.voiceDescription'),
          icon: createIcon(SettingsIcon),
        },
        {
          title: t('trialPaywall.benefit.creator.workflowTitle'),
          description: t('trialPaywall.benefit.creator.workflowDescription'),
          icon: createIcon(UsersIcon),
        },
      ],
      minutesDescription: t('trialPaywall.benefit.creator.minutesDescription'),
    },
    'Creator Pro': {
      features: [
        {
          title: t('trialPaywall.benefit.creatorPro.lipSyncTitle'),
          description: t('trialPaywall.benefit.creatorPro.lipSyncDescription'),
          icon: createIcon(FaceWinkIcon),
        },
        {
          title: t('trialPaywall.benefit.creatorPro.editsTitle'),
          description: t('trialPaywall.benefit.creatorPro.editsDescription'),
          icon: createIcon(SettingsIcon),
        },
        {
          title: t('trialPaywall.benefit.creatorPro.teamTitle'),
          description: t('trialPaywall.benefit.creatorPro.teamDescription'),
          icon: createIcon(UsersIcon),
        },
      ],
      minutesDescription: t('trialPaywall.benefit.creatorPro.minutesDescription'),
    },
  };

  const enterpriseBenefits: PaywallBenefit[] = [
    {
      title: t('trialPaywall.benefit.enterprise.minutesTitle'),
      description: t('trialPaywall.benefit.enterprise.minutesDescription'),
      icon: createIcon(CheckMarkIcon),
    },
    {
      title: t('trialPaywall.benefit.enterprise.reviewTitle'),
      description: t('trialPaywall.benefit.enterprise.reviewDescription'),
      icon: createIcon(VoiceIcon),
    },
    {
      title: t('trialPaywall.benefit.enterprise.procurementTitle'),
      description: t('trialPaywall.benefit.enterprise.procurementDescription'),
      icon: createIcon(FileUploadIcon),
    },
    {
      title: t('trialPaywall.benefit.enterprise.supportTitle'),
      description: t('trialPaywall.benefit.enterprise.supportDescription'),
      icon: createIcon(UsersIcon),
    },
  ];

  return { allPlansInclude, chipLabels, enterpriseBenefits, planBenefits };
};
